import { useState, useEffect } from "react";
import * as Sentry from "@sentry/react";
import { useAuth } from "../contexts/AuthContext";
import { useParams,useNavigate } from "react-router-dom";
import api from "../api/client";
import LoadingScreen from "../components/LoadingScreen";
import Card from "../components/Card";
import Button from "../components/Button";
import Tag from "../components/Tag";
import DateRangePicker from "../components/DateRangePicker";
import Map from "../components/Map";
import Timeline from "../components/Timeline";
import proj4 from "proj4";
import "./styles.css";

proj4.defs(
    "EPSG:25830",
    "+proj=utm +zone=30 +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs",
);

const formatUtm = (lat, lng) => {
    if (lat === null || lat === undefined || lng === null || lng === undefined) {
        return null;
    }
    const [x, y] = proj4("EPSG:4326", "EPSG:25830", [
        parseFloat(lng),
        parseFloat(lat),
    ]);
    if (!Number.isFinite(x) || !Number.isFinite(y)) return null;
    return { x: Math.round(x), y: Math.round(y) };
};

export default function EspecieDetalle() {
    const { id } = useParams();
    const { user, permissions } = useAuth();
    const navigate = useNavigate();
    const [captura, setCaptura] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [frontImageError, setFrontImageError] = useState(false);
    const [backImageError, setBackImageError] = useState(false);

    useEffect(() => {
        fetchCaptura();
    }, [id]);

    const fetchCaptura = async () => {
        try {
            setLoading(true);
            const response = await api.get(`/capturas/${id}`);
            setCaptura(response.data.captura || response.data);
            setFrontImageError(false);
            setBackImageError(false);
        } catch (err) {
            Sentry.captureException(new Error('Error loading captura: ' + err.message));
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleImageError = (e, side) => {
        const imageUrl = e.target.src;
        if (side === "front") {
            setFrontImageError(true);
        } else if (side === "back") {
            setBackImageError(true);
        }
        Sentry.captureException(new Error(`Error loading image: ${imageUrl}`));
    };

    if (loading) return <LoadingScreen />;
    if (error) return <div className="error-message">Error: {error}</div>;
    if (!captura) return <div>No se encontró la captura</div>;

    return (
        <div className="page-especie-detalle">
            <div className="page-header-detalle">
                <div className="page-title-section">
                    <h1 className="page-title-detalle">{captura.especie}</h1>
                    <div className="breadcrumbs">
                        <button
                            className="breadcrumb-link"
                            onClick={() => navigate("/capturas")}
                        >
                            Capturas
                        </button>
                        <span className="breadcrumb-separator"> &gt; </span>
                        <span className="breadcrumb-current">
                            {captura.especie}
                        </span>
                    </div>
                </div>
            </div>

            <div className="page-content-detalle">
                <div className="page-grid-detalle">
                    <div className="page-grid-left-detalle">
                        <Card>
                            <div className="card-content">
                                <h3 className="card-title-detalle">
                                    Datos de la captura
                                </h3>
                                {(user.rol !== 'provincial' && user.rol !== 'gestor' && user.rol !== 'titular') && (
                                    <>
                                    {(captura.urlImagenFront || captura.urlImagenBack) && (
                                        <div className="capture-image-container">
                                            {captura.urlImagenFront && !frontImageError && (
                                                <img
                                                    src={captura.urlImagenFront}
                                                    alt={`${captura.especie} - Frente`}
                                                    className="capture-image"
                                                    onError={(e) => handleImageError(e, "front")}
                                                />
                                            )}
                                            {captura.urlImagenBack && !backImageError && (
                                                <img
                                                    src={captura.urlImagenBack}
                                                    alt={`${captura.especie} - Dorso`}
                                                    className="capture-image"
                                                    onError={(e) => handleImageError(e, "back")}
                                                />
                                            )}
                                        </div>
                                    )}
                                    </>
                                )}
                                <div className="capture-details">
                                    <div className="detail-row">
                                        <span className="detail-label">
                                            Especie
                                        </span>
                                        <span className="detail-value">
                                            {captura.especie}
                                        </span>
                                    </div>
                                    <div className="detail-row">
                                        <span className="detail-label">
                                            Sexo
                                        </span>
                                        <div className="detail-value-with-icon">
                                            <span>
                                                {captura.sexo === "m"
                                                    ? "Macho"
                                                    : captura.sexo === "f"
                                                        ? "Hembra"
                                                        : "Desconocido"}
                                            </span>
                                            {captura.sexo === "m" && (
                                                <span className="sex-icon">
                                                    ♂
                                                </span>
                                            )}
                                            {captura.sexo === "f" && (
                                                <span className="sex-icon">
                                                    ♀
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                    <div className="detail-row">
                                        <span className="detail-label">
                                            N° Precinto
                                        </span>
                                        <span className="detail-value">
                                            {captura.precinto}
                                        </span>
                                    </div>
                                    <div className="detail-row">
                                        <span className="detail-label">
                                            Tipo
                                        </span>
                                        <span className="detail-value">
                                            {" "}
                                            <Tag
                                                variant={
                                                    captura.tipo ===
                                                    "automatica"
                                                        ? "success"
                                                        : "info"
                                                }
                                            >
                                                {captura.tipo === "automatica"
                                                        ? "CapturCYL"
                                                        : captura.tipo === "null" || !captura.tipo
                                                        ? "Papel"
                                                        : "No especificado"}
                                            </Tag>{" "}
                                        </span>
                                    </div>
                                    <div className="detail-row">
                                        <span className="detail-label">
                                            Modalidad
                                        </span>
                                        <span className="detail-value">
                                            {captura.modalidad || "No especificada"}
                                        </span>
                                    </div>
                                    <div className="detail-row">
                                        <span className="detail-label">
                                            Coto
                                        </span>
                                        <span className="detail-value">
                                            {" "}
                                            <Tag variant="success" style={{ flex: "auto" }}>
                                                {captura.coto}
                                            </Tag>{" "}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </Card>
                    </div>

                    <div className="page-grid-right-detalle">
                        <Card>
                            <div className="card-content">
                                <h3 className="card-title-detalle">
                                    Ubicación de la captura
                                </h3>
                                {(user?.rol === 'admin' || user?.rol === 'junta') && (
                                    <>
                                        {captura.lat !== null && captura.lng !== null ? (
                                            <div className="detalle-map-box">
                                                <Map
                                                    lat={captura.lat}
                                                    lng={captura.lng}
                                                    precinto={captura.precinto}
                                                    especie={captura.especie}
                                                    sexo={captura.sexo}
                                                    gpsError={captura.gpsError}
                                                    cotoPolygon={captura.cotoPolygon}
                                                />
                                            </div>
                                        ) : (
                                            <div
                                                className="detalle-map-box"
                                                style={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    color: "var(--neutral-500)",
                                                    background: "var(--gray-200)",
                                                }}
                                            >
                                                Coordenadas no disponibles
                                            </div>
                                        )}
                                    </>
                                )}
                                <div className="location-details">
                                    {(user?.rol === 'admin' || user?.rol === 'junta') && (
                                        <>
                                            <div className="detail-row">
                                                <span className="detail-label">
                                                    Coordenadas (UTM ETRS89 30N)
                                                </span>
                                                <span className="detail-value">
                                                    {(() => {
                                                        const utm = formatUtm(captura.lat, captura.lng);
                                                        return utm
                                                            ? `X: ${Math.round(utm.x)} · Y: ${Math.round(utm.y)}`
                                                            : "No determinado";
                                                    })()}
                                                </span>
                                            </div>
                                            <div className="detail-row">
                                                <span className="detail-label">
                                                    Error GPS
                                                </span>
                                                <span className="detail-value">
                                                    {captura.gpsError !== null && captura.gpsError !== undefined
                                                        ? `${parseFloat(captura.gpsError).toFixed(2)} m`
                                                        : "No determinado"}
                                                </span>
                                            </div>
                                            <div className="detail-row">
                                                <span className="detail-label">
                                                    Ubicación respecto al coto
                                                </span>
                                                <span className="detail-value">
                                                    {captura.dentroCoto === true && (
                                                        <Tag variant="success">Dentro del coto</Tag>
                                                    )}
                                                    {captura.dentroCoto === false && (
                                                        <Tag variant="error">Fuera del coto</Tag>
                                                    )}
                                                    {(captura.dentroCoto === null || captura.dentroCoto === undefined) && (
                                                        <span style={{ color: "var(--neutral-500)" }}>
                                                            No determinado
                                                        </span>
                                                    )}
                                                </span>
                                            </div>
                                        </>
                                    )}
                                    <div className="detail-row">
                                        <span className="detail-label">
                                            Fecha y hora caza
                                        </span>
                                        <span className="detail-value">
                                            {captura.fechaCazada || "No determinada"}
                                        </span>
                                    </div>
                                    <div className="detail-row">
                                        <span className="detail-label">
                                            Cazador
                                        </span>
                                        <span className="detail-value">
                                            {captura.cazador}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </Card>
                    </div>
                </div>

                {/* <div className="timeline-section">
          <Card>
            <div className="card-content">
              <h3 className="card-title-detalle">Timeline activación y desactivación del precinto</h3>
              <div className="timeline-filters">
                <div className="filter-group">
                  <label className="filter-label">Seleccione un rango de fechas</label>
                  <DateRangePicker
                    startDate="01/02/2025"
                    endDate="28/02/2025"
                    // onChange={setDateRange}
                  />
                </div>
                <div className="filter-actions">
                  <Button variant="accent" onClick={() => {}}>
                    <img src={iconDownload} alt="" className="button-icon button-icon-white" />
                    <span>Descargar</span>
                  </Button>

                </div>
              </div>
              <div className="timeline-chart-container">
                <Timeline data={timelineData} />
              </div>
            </div>
          </Card>
        </div> */}
            </div>
        </div>
    );
}


